import { BsRangeError, bsToAd, formatBsDate, instantToAdCivil, instantToBs, type BsDate } from '@ministry/nepali-date';

/**
 * DESIGN_SYSTEM.md §3: "BS is primary, AD is secondary in every date display ... BS
 * always leads, AD always follows in parentheses, in both locales." Before this file
 * existed, the File Register / File Detail / My Tasks due-date cell rendered
 * `new Date(dueAt).toLocaleDateString(...)` — a bare Gregorian date, in `M/D/YYYY`
 * format, sitting in the same row as `registeredDateBs` (a BS-only string like
 * `2083-06-02`). Two calendars, two formats, one row — the exact defect §3 exists to
 * prevent (ux-review finding, `/design`). This module is the ONE place either kind of
 * date gets its display string, so no screen re-invents the BS/AD pairing rule.
 *
 * `@ministry/nepali-date` THROWS (`BsRangeError`) for any instant outside
 * AD 1943-04-14..2027-04-13 / BS 2000-01-01..2083-12-30 — it never guesses a date the
 * published calendar does not settle (see packages/nepali-date/README.md). A due date
 * far enough in the future can fall outside that window; every function below catches
 * exactly that error and falls back to AD-only, explicitly, rather than crashing a row
 * or fabricating a BS date the package itself refuses to invent.
 */

/** The one clock every displayed Nepali government timestamp is read on. */
const KATHMANDU = 'Asia/Kathmandu';

function isoFromAdCivil(ad: { year: number; month: number; day: number }): string {
  return `${String(ad.year).padStart(4, '0')}-${String(ad.month).padStart(2, '0')}-${String(ad.day).padStart(2, '0')}`;
}

/** Parses the server's `registeredDateBs` shape (`"2083-06-02"`) into a `BsDate`. */
function parseBsIsoString(value: string): BsDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

/**
 * Renders an AD instant (e.g. `dueAt`) as BS-primary, AD-secondary: `2 Ashwin 2083
 * (2026-09-18)` in English, `२ आश्विन २०८३ (2026-09-18)` in Nepali. Falls back to the
 * AD date alone — still on the Kathmandu wall clock, never `toLocaleDateString`'s
 * host-locale Gregorian formatting — when the instant is outside the calendar's
 * published coverage.
 */
/**
 * Same BS-primary/AD-secondary rendering as `formatDueDisplay`, under a name that doesn't
 * imply "deadline" — used wherever an arbitrary instant (a notification's `createdAt`, an
 * attachment's `uploadedAt`) needs the same one display convention every other date in this
 * app uses, rather than a screen inventing its own `toLocaleDateString` call.
 */
export function formatInstantDisplay(iso: string, locale: 'en' | 'ne'): string {
  return formatDueDisplay(iso, locale);
}

/**
 * ============================================================================
 * R-053 — an AUDIT instant, with the time of day, on the Kathmandu wall clock.
 *
 * `formatInstantDisplay` delegates to `formatDueDisplay` and renders a DATE only. That
 * is right for a due date, which has no meaningful time, and wrong for an audit record,
 * where the time is most of the value: two events on the same day are indistinguishable,
 * their order is unreadable, and "when exactly did this ministry get suspended" — the
 * question an audit log exists to answer — cannot be answered from the screen.
 *
 * The clock is `Asia/Kathmandu` explicitly, not the viewer's browser. An auditor in
 * another timezone reading a Nepali government record must see the time the action
 * happened *in Nepal*, or two people reading the same row disagree about what it says.
 * 24-hour, because an audit trail is not the place for an am/pm ambiguity.
 * ============================================================================
 */
export function formatInstantWithTime(iso: string, locale: 'en' | 'ne'): string {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return iso;
  const datePart = formatInstantDisplay(iso, locale);
  let timePart: string;
  try {
    timePart = new Intl.DateTimeFormat(locale === 'ne' ? 'ne-NP' : 'en-GB', {
      timeZone: KATHMANDU,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(instant);
  } catch {
    // A runtime with no Asia/Kathmandu data: show the date rather than a time that
    // would silently be in some other zone. A wrong time is worse than no time.
    return datePart;
  }
  return `${datePart} ${timePart}`;
}

export function formatDueDisplay(iso: string, locale: 'en' | 'ne'): string {
  const instant = new Date(iso);
  let adLabel: string;
  try {
    adLabel = isoFromAdCivil(instantToAdCivil(instant));
  } catch {
    // Only reachable if `instant` itself is invalid (bad ISO string) or the runtime
    // cannot resolve Asia/Kathmandu at all — instantToBs below will throw the same
    // way, at which point the row still needs SOMETHING to show.
    adLabel = instant.toISOString().slice(0, 10);
  }
  try {
    const bsLabel = formatBsDate(instantToBs(instant), { locale, style: 'long' });
    return `${bsLabel} (${adLabel})`;
  } catch (err) {
    if (err instanceof BsRangeError) return adLabel;
    throw err;
  }
}

/**
 * Renders the server's already-BS `registeredDateBs` string with its AD equivalent
 * appended in parentheses, matching `formatDueDisplay`'s convention so the two columns
 * in the same row read as the same calendar system at a glance. Falls back to the raw
 * BS string (never blank, never a thrown error inside a table cell) if the value is
 * malformed or — in principle, for a very old seeded record — outside coverage.
 */
export function formatRegisteredDisplay(registeredDateBs: string, locale: 'en' | 'ne'): string {
  const bs = parseBsIsoString(registeredDateBs);
  if (!bs) return registeredDateBs;
  try {
    const adLabel = isoFromAdCivil(bsToAd(bs));
    return `${formatBsDate(bs, { locale, style: 'long' })} (${adLabel})`;
  } catch {
    return registeredDateBs;
  }
}

/**
 * Nepal has one fixed offset year-round (+05:45, no DST) — combining a BS calendar date
 * (picked via `DatePicker`) with a wall-clock time (`HH:mm`, picked via a native
 * `<input type="time">`) into the UTC instant `scheduledStart` requires on the wire
 * (API_CONTRACT §12: "an instant on the wire is ISO-8601 UTC with `Z`. A BS date string is
 * never accepted"). Used by the Appointment Inbox's accept/reschedule flow (I14) — the only
 * screen in this build that has to construct an instant from a BS date rather than only
 * ever display one. Throws `BsRangeError` outside the calendar's published coverage,
 * exactly like every other function in this file; the caller is expected to have already
 * constrained the `DatePicker` to a reachable range (appointments are booked at most
 * `horizonDays` ahead, always inside coverage in practice).
 */
export function bsDateTimeToInstant(bs: BsDate, hour: number, minute: number): Date {
  const ad = bsToAd(bs);
  const utcMillis = Date.UTC(ad.year, ad.month - 1, ad.day, hour, minute) - (5 * 60 + 45) * 60_000;
  return new Date(utcMillis);
}
